import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface TooltipCandidate {
  id: string;
  priority: number;
  sequence: number;
}

interface MapTooltipContextValue {
  activeTooltipId?: string;
  showTooltip: (id: string, priority: number) => void;
  hideTooltip: (id: string) => void;
}

const MapTooltipContext = createContext<MapTooltipContextValue | undefined>(
  undefined
);

export const RUNWAY_TOOLTIP_PRIORITY = 0;
export const MARKER_TOOLTIP_PRIORITY = 1;

export const MapTooltipProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [candidates, setCandidates] = useState<TooltipCandidate[]>([]);
  const sequence = useRef(0);

  const showTooltip = useCallback((id: string, priority: number) => {
    sequence.current += 1;
    const nextCandidate = { id, priority, sequence: sequence.current };
    setCandidates((current) => [
      ...current.filter((candidate) => candidate.id !== id),
      nextCandidate,
    ]);
  }, []);

  const hideTooltip = useCallback((id: string) => {
    setCandidates((current) =>
      current.filter((candidate) => candidate.id !== id)
    );
  }, []);

  const activeTooltipId = useMemo(
    () =>
      candidates.reduce<TooltipCandidate | undefined>((active, candidate) => {
        if (
          !active ||
          candidate.priority > active.priority ||
          (candidate.priority === active.priority &&
            candidate.sequence > active.sequence)
        ) {
          return candidate;
        }
        return active;
      }, undefined)?.id,
    [candidates]
  );

  const value = useMemo(
    () => ({ activeTooltipId, showTooltip, hideTooltip }),
    [activeTooltipId, hideTooltip, showTooltip]
  );

  return (
    <MapTooltipContext.Provider value={value}>
      {children}
    </MapTooltipContext.Provider>
  );
};

export const useMapTooltip = (tooltipId: string, priority: number) => {
  const context = useContext(MapTooltipContext);
  const [localVisible, setLocalVisible] = useState(false);
  const showSharedTooltip = context?.showTooltip;
  const hideSharedTooltip = context?.hideTooltip;

  const showTooltip = useCallback(() => {
    if (showSharedTooltip) {
      showSharedTooltip(tooltipId, priority);
    } else {
      setLocalVisible(true);
    }
  }, [priority, showSharedTooltip, tooltipId]);

  const hideTooltip = useCallback(() => {
    if (hideSharedTooltip) {
      hideSharedTooltip(tooltipId);
    } else {
      setLocalVisible(false);
    }
  }, [hideSharedTooltip, tooltipId]);

  useEffect(
    () => () => hideSharedTooltip?.(tooltipId),
    [hideSharedTooltip, tooltipId]
  );

  return {
    showTooltip,
    hideTooltip,
    isTooltipVisible: context
      ? context.activeTooltipId === tooltipId
      : localVisible,
  };
};
